export interface HourlySums {
  clicks: number;
  cost: number;
  leadOk: number;
  leadKo: number;
  leadAR: number;
  totalLead: number;
  leadDoi: number;
  leadP0: number;
  leadCoregOk: number;
  leadCoregAAOk: number;
  leadCrossOk: number;
  checkout: number;
  sales: number;
  turnover: number;
  subscription: number;
  renewal: number;
  refund: number;
  chargeback: number;
}

export type HourlyMetricFormat = 'count' | 'money' | 'percent';

export type HourlyMetricGroup = 'Traffic' | 'Leads' | 'Conversions';

export interface HourlyMetric {
  key: string;
  label: string;
  group: HourlyMetricGroup;
  format: HourlyMetricFormat;
  lowerIsBetter?: boolean;
  costBased?: boolean;
  compute: (sums: HourlySums) => number | null;
}

const ratio = (value: number, total: number): number | null => total ? value / total : null;

export const HourlyMetricGroups: HourlyMetricGroup[] = ['Traffic', 'Leads', 'Conversions'];

export const HourlyMetrics: HourlyMetric[] = [
  {key: 'clicks', label: 'Clicks', group: 'Traffic', format: 'count', compute: s => s.clicks},
  {key: 'cost', label: 'Cost', group: 'Traffic', format: 'money', lowerIsBetter: true, costBased: true, compute: s => s.cost},
  {key: 'cpc', label: 'EPC / CPC', group: 'Traffic', format: 'money', lowerIsBetter: true, costBased: true, compute: s => ratio(s.cost, s.clicks)},
  {key: 'cpl', label: 'CPL', group: 'Traffic', format: 'money', lowerIsBetter: true, costBased: true, compute: s => ratio(s.cost, s.leadOk)},

  {key: 'leadOk', label: 'Lead OK', group: 'Leads', format: 'count', compute: s => s.leadOk},
  {key: 'leadOkPercent', label: 'Lead OK (%)', group: 'Leads', format: 'percent', compute: s => ratio(s.leadOk, s.clicks)},
  {key: 'leadKo', label: 'Lead KO', group: 'Leads', format: 'count', lowerIsBetter: true, compute: s => s.leadKo},
  {key: 'leadAR', label: 'AR', group: 'Leads', format: 'count', compute: s => s.leadAR},
  {key: 'leadARPercent', label: 'AR (%)', group: 'Leads', format: 'percent', compute: s => ratio(s.leadAR, s.totalLead)},
  {key: 'leadDoi', label: 'DOI', group: 'Leads', format: 'count', compute: s => s.leadDoi},
  {key: 'leadDoiPercent', label: 'DOI (%)', group: 'Leads', format: 'percent', compute: s => ratio(s.leadDoi, s.leadOk)},
  {key: 'leadP0', label: 'P0', group: 'Leads', format: 'count', compute: s => s.leadP0},
  {key: 'leadP0Percent', label: 'P0 (%)', group: 'Leads', format: 'percent', compute: s => ratio(s.leadP0, s.clicks)},
  {key: 'leadCoregOk', label: 'P2', group: 'Leads', format: 'count', compute: s => s.leadCoregOk},
  {key: 'leadCoregOkPercent', label: 'P2 (%)', group: 'Leads', format: 'percent', compute: s => ratio(s.leadCoregOk, s.leadOk + s.leadAR)},
  {key: 'leadCoregAAOk', label: 'AA', group: 'Leads', format: 'count', compute: s => s.leadCoregAAOk},
  {key: 'leadCoregAAOkPercent', label: 'AA (%)', group: 'Leads', format: 'percent', compute: s => ratio(s.leadCoregAAOk, s.leadOk + s.leadAR)},
  {key: 'leadCrossOk', label: 'Xsell', group: 'Leads', format: 'count', compute: s => s.leadCrossOk},
  {key: 'leadCrossOkPercent', label: 'Xsell (%)', group: 'Leads', format: 'percent', compute: s => ratio(s.leadCrossOk, s.leadOk + s.leadAR)},

  {key: 'checkout', label: 'Checkout', group: 'Conversions', format: 'count', compute: s => s.checkout},
  {key: 'sales', label: 'Sales', group: 'Conversions', format: 'count', compute: s => s.sales},
  {key: 'turnover', label: 'Turnover', group: 'Conversions', format: 'money', compute: s => s.turnover},
  {key: 'subscription', label: 'Subscription', group: 'Conversions', format: 'count', compute: s => s.subscription},
  {key: 'renewal', label: 'Renewal', group: 'Conversions', format: 'count', compute: s => s.renewal},
  {key: 'refund', label: 'Refund', group: 'Conversions', format: 'count', lowerIsBetter: true, compute: s => s.refund},
  {key: 'chargeback', label: 'Chargeback', group: 'Conversions', format: 'count', lowerIsBetter: true, compute: s => s.chargeback},
];

export const HourlyDefaultMetricKeys = [
  'clicks',
  'cost',
  'leadOk',
  'leadOkPercent',
  'leadP0',
  'leadCoregOk',
  'leadCoregAAOk',
  'leadCrossOk',
  'sales',
  'turnover',
];

export function createEmptySums(): HourlySums {
  return {
    clicks: 0,
    cost: 0,
    leadOk: 0,
    leadKo: 0,
    leadAR: 0,
    totalLead: 0,
    leadDoi: 0,
    leadP0: 0,
    leadCoregOk: 0,
    leadCoregAAOk: 0,
    leadCrossOk: 0,
    checkout: 0,
    sales: 0,
    turnover: 0,
    subscription: 0,
    renewal: 0,
    refund: 0,
    chargeback: 0,
  };
}

export function toSums(row: any): HourlySums {
  const goal = (name: string) => row.goals?.[name]?.totals ?? {count: 0, amount: 0};

  return {
    clicks: Number(row.clicks) || 0,
    cost: Number(row.cost) || 0,
    leadOk: Number(row.leadOk) || 0,
    leadKo: Number(row.leadKo) || 0,
    leadAR: Number(row.leadAR) || 0,
    totalLead: Number(row.totalLead) || 0,
    leadDoi: Number(row.leadDoi) || 0,
    leadP0: Number(row.leadP0) || 0,
    leadCoregOk: Number(row.leadCoregOk) || 0,
    leadCoregAAOk: Number(row.leadCoregAAOk) || 0,
    leadCrossOk: Number(row.leadCrossOk) || 0,
    checkout: goal('checkout').count,
    sales: goal('sale').count,
    turnover: goal('sale').amount,
    subscription: goal('subscription').count,
    renewal: goal('renewal').count,
    refund: goal('refund').count,
    chargeback: goal('chargeback').count,
  };
}

export function addSums(target: HourlySums, source: HourlySums): HourlySums {
  for (const key of Object.keys(target) as (keyof HourlySums)[]) {
    target[key] += source[key];
  }

  return target;
}

export function scaleSums(sums: HourlySums, divisor: number): HourlySums {
  const scaled = createEmptySums();

  for (const key of Object.keys(scaled) as (keyof HourlySums)[]) {
    scaled[key] = divisor ? sums[key] / divisor : 0;
  }

  return scaled;
}
